import React from "react";
import { Box, Grid, Typography, Button, Alert } from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import InformationTile from "components/Shared/InformationTile";
import Status from "components/Shared/Status";
import FormDrawer from "components/Shared/FormDrawer";
import { useEntityDetailContext } from "contexts/EntityDetailContext";
import {
    useCreateDocumentationFeeMutation,
    useUpdateDocumentationFeeMutation,
} from "store/services/entityApi";
import { toast } from "react-toastify";
import DocumentationFeeForm from "./DocumentationFeeForm";

const DocumentationFeeTab = () => {
    const { entityDetail, loadingEntity } = useEntityDetailContext();
    const [showForm, setShowForm] = React.useState(false);

    const documentationFee = entityDetail?.documentationFee || null;

    const [createFee, { isLoading: creating }] =
        useCreateDocumentationFeeMutation();

    const [updateFee, { isLoading: updating }] =
        useUpdateDocumentationFeeMutation();

    const handleSubmit = async (payload) => {
        try {
            if (documentationFee) {
                await updateFee({
                    entityId: entityDetail._id,
                    documentationFee: payload.documentationFee,
                    isActive: payload.isActive,
                }).unwrap();

                toast.success("Documentation fee updated successfully!");
            } else {
                await createFee({
                    entityId: entityDetail._id,
                    documentationFee: payload.documentationFee,
                    isActive: payload.isActive,
                }).unwrap();

                toast.success("Documentation fee created successfully!");
            }

            setShowForm(false);
        } catch (err) {
            console.error("Documentation fee error:", err);
            toast.error(err?.data?.message || err?.message || "Operation failed!");
        }
    };

    return (
        <>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" p={3}>
                <Typography variant="h6">
                    Documentation and Processing Fee
                    <Alert severity="info" sx={{ mt: 1 }}>
                        This is a flat fee (fixed amount, not a percentage) added on top of
                        the shipment price as a documentation and processing charge.
                    </Alert>
                </Typography>

                <Button
                    variant="outlined"
                    onClick={() => setShowForm(true)}
                    disabled={loadingEntity}
                >
                    {documentationFee ? "Update Fee" : "Add Fee"}
                </Button>
            </Box>

            {/* If loading */}
            {loadingEntity ? (
                <Typography px={3}>Loading...</Typography>
            ) : (
                <Box padding={3}>
                    <Grid container spacing={4}>

                        <Grid item xs={12} md={6} lg={4}>
                            <InformationTile
                                icon={() => <DescriptionOutlined color="primary" />}
                                title="Documentation and Processing Fee"
                                subtitle={
                                    documentationFee?.documentationFee !== undefined &&
                                        documentationFee?.documentationFee !== null
                                        ? documentationFee.documentationFee.toLocaleString()
                                        : "Not Set"
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <InformationTile
                                icon={() => <InfoOutlined color="primary" />}
                                title="Status"
                            >
                                <Status isActive={documentationFee?.isActive ?? false} />
                            </InformationTile>
                        </Grid>

                    </Grid>
                </Box>
            )}

            {/* FORM DRAWER */}
            <FormDrawer
                title={documentationFee ? "Update Documentation Fee" : "Add Documentation Fee"}
                open={showForm}
                onClose={() => setShowForm(false)}
            >
                <DocumentationFeeForm
                    documentationFeeData={documentationFee}
                    entityId={entityDetail?._id}
                    isBusy={creating || updating}
                    onCancel={() => setShowForm(false)}
                    onSubmit={handleSubmit}
                />
            </FormDrawer>
        </>
    );
};

export default DocumentationFeeTab;
